"use client";

import type {
  StripeCustomerID,
  StripeSubscriptionID,
} from "@repo/db/branded-types";
import { STATSIG_EVENTS } from "@repo/statsig";
import * as Sentry from "@sentry/nextjs";
import { useStatsigClient } from "@repo/statsig";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeElementsOptions,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import {
  createSubscriptionForCheckout,
  type CreateSubscriptionForCheckoutResponse,
} from "../actions/checkout-subscription";
import {
  validatePromoCode,
  type PromoCodeValidation,
} from "../actions/validate-promo-code";
import { cancelSubscription } from "../actions/subscription";
import { useElementsTheme } from "../context/ElementsThemeContext";
import getStripe from "../context/utils";
import { normalizeProductKey } from "../lib/normalize-product";

interface PaymentCompleteData {
  customerId: StripeCustomerID;
  subscriptionId: StripeSubscriptionID;
  /** Whether this checkout started a free trial (setup_intent = charge
   *  deferred to trial end). Omitted when unknown (existing subscription). */
  isTrialing?: boolean;
}

interface ElementsCheckoutFormProps {
  priceId: string;
  planPrice: number;
  planInterval: "month" | "year";
  trialDays: number;
  /** Display name (e.g. "Owlet OnCall") — used for the Stripe customer
   *  description and any human-readable copy. */
  productName: string;
  /** Internal product identifier (e.g. "pediatricSupportFamily") — used for
   *  analytics product_key after passing through `normalizeProductKey`.
   *  Must reflect the actual selected plan, not the brand name, otherwise
   *  payment_submitted reports `product_key: "other"` for everyone. */
  productKey: string;
  userInfo: {
    email?: string;
    name: string;
    metadata: Record<string, string>;
  };
  onPaymentComplete: (data: PaymentCompleteData) => void;
  onError?: (error: Error) => void;
  onPaymentElementReady?: () => void;
}

const FORM_ID = "elements-checkout-form";

/** Best-effort cancel; swallows errors and reports to Sentry. */
async function tryCancelDanglingSubscription(
  subscriptionId: StripeSubscriptionID
) {
  try {
    await cancelSubscription(subscriptionId);
  } catch (cancelErr) {
    Sentry.captureException(cancelErr, {
      tags: {
        component: "stripe.elementsCheckout",
        stripe_operation: "cancel_dangling_subscription",
      },
      extra: { subscriptionId },
    });
  }
}

/**
 * First-invoice charge after a coupon, in cents — mirrors Stripe's per-invoice
 * discount math (percent rounded to the nearest cent, then subtracted) so the
 * mounted Elements amount can track the discounted PaymentIntent on non-trial
 * checkouts (PE-1538).
 */
function discountedImmediateCents(
  fullCents: number,
  promo: PromoCodeValidation
): number {
  if (promo.percentOff != null) {
    return Math.max(
      0,
      fullCents - Math.round((fullCents * promo.percentOff) / 100)
    );
  }
  if (promo.amountOffCents != null) {
    return Math.max(0, fullCents - promo.amountOffCents);
  }
  return fullCents;
}

/**
 * Inner form component that uses Stripe hooks (must be inside <Elements>).
 * Reads visual config from ElementsThemeProvider wrapper.
 */
function CheckoutForm({
  priceId,
  planPrice,
  planInterval,
  trialDays,
  productName,
  productKey,
  userInfo,
  onPaymentComplete,
  onError,
  onPaymentElementReady,
}: ElementsCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { logEvent } = useStatsigClient();
  const {
    renderSubmitButton,
    errorClassName,
    renderTosCheckbox,
    enablePromoCodes,
  } = useElementsTheme();

  // Fire checkout_method_loaded once when the embedded form mounts. The
  // Express Checkout sibling fires its own load event when ready (see
  // ExpressCheckoutButton.handleReady). Dashboards split the funnel by
  // value (express|embedded) and load_count.
  useEffect(() => {
    logEvent({
      eventName: STATSIG_EVENTS.CHECKOUT_METHOD_LOADED,
      value: "embedded",
      metadata: {
        load_count: "1",
        trigger: "initial",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        product_key: normalizeProductKey(productKey),
        billing_period: planInterval === "year" ? "annual" : "monthly",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const requireTos = !!renderTosCheckbox;
  const [tosAccepted, setTosAccepted] = useState(!requireTos);
  const [tosError, setTosError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Promo code (PE-1538) — opt-in via theme.enablePromoCodes.
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<PromoCodeValidation | null>(
    null
  );
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);

  async function handleApplyPromo() {
    const code = promoInput.trim();
    if (!code || promoChecking) return;
    setPromoChecking(true);
    setPromoError(null);
    try {
      const result = await validatePromoCode(code);
      if (!result.ok) {
        setAppliedPromo(null);
        setPromoError(result.error ?? "That promo code isn't valid.");
        return;
      }
      setAppliedPromo(result);
      // Non-trial: the coupon discounts the first invoice, so keep the mounted
      // Elements amount in sync with the discounted PaymentIntent (Stripe
      // rejects a confirm whose amount doesn't match). A trial charges $0 now
      // via a SetupIntent (amount is display-only) and a fully-zeroed invoice
      // skips confirmation — neither needs the update.
      if (trialDays === 0 && elements) {
        const next = discountedImmediateCents(planPrice, result);
        if (next > 0) elements.update({ amount: next });
      }
    } finally {
      setPromoChecking(false);
    }
  }

  function handleRemovePromo() {
    setAppliedPromo(null);
    setPromoError(null);
    setPromoInput("");
    if (trialDays === 0 && elements) elements.update({ amount: planPrice });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!stripe || !elements) return;

    // Validate ToS — opt-in via theme.renderTosCheckbox.
    if (requireTos && !tosAccepted) {
      setTosError(true);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setTosError(false);

    let result: CreateSubscriptionForCheckoutResponse | undefined;

    try {
      // Step 1: Validate payment details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Please check your payment details.");
        setIsSubmitting(false);
        return;
      }

      // Fire payment_submitted at click-intent — BEFORE createSubscription
      // and stripe.confirmPayment. Captures the moment the user committed,
      // independent of downstream server / Stripe outcomes. A failure in
      // createSubscriptionForCheckout (e.g. a Stripe API-version change
      // that breaks invoice.payment_intent vs invoice.confirmation_secret)
      // previously blanked the front half of the funnel for ~6 days in
      // prod because logEvent ran after the server call. payment_succeeded
      // still fires only on confirm-success below.
      const paymentMetadata = {
        product_key: normalizeProductKey(productKey),
        billing_period: planInterval === "year" ? "annual" : "monthly",
        flow: "elements_checkout",
        // planPrice is already in cents; mirror paywall cta_clicked +
        // webhook subscription_created so funnel Sum metrics work uniformly.
        price_cents: String(planPrice),
        implied_mrr_cents: String(
          planInterval === "year" ? Math.round(planPrice / 12) : planPrice
        ),
        implied_immediate_revenue_cents: String(planPrice),
      };
      logEvent({
        eventName: STATSIG_EVENTS.PAYMENT_SUBMITTED,
        value: "card",
        metadata: paymentMetadata,
      });
      // Signal payment/page.tsx so its unmount cleanup skips the
      // checkout_abandoned event — the user did submit, even if the card
      // later fails. Listener:
      // `apps/platform/src/app/home/steps/payment/page.tsx`.
      window.dispatchEvent(new Event("statsig:payment_submitted"));

      // Step 2: Create subscription on server
      result = await createSubscriptionForCheckout({
        email: userInfo.email,
        name: userInfo.name,
        metadata: userInfo.metadata,
        planId: priceId,
        planPrice,
        planInterval,
        trialDays,
        productName,
        promotionCode: appliedPromo?.promotionCodeId,
      });

      // Step 3: Confirm payment inline
      const returnUrl = window.location.href;

      // Stripe requires email in confirmParams.payment_method_data.billing_details.
      const billingDetails = {
        ...(userInfo.email ? { email: userInfo.email } : {}),
        ...(userInfo.name ? { name: userInfo.name } : {}),
      };

      if (
        result.type === "existing_subscription" ||
        result.type === "no_payment_required"
      ) {
        // existing_subscription: customer already subscribed.
        // no_payment_required: a coupon zeroed the (non-trial) invoice, so
        // Stripe activated the sub with no PaymentIntent to confirm (PE-1538).
        // Either way, skip confirmation and treat as success so the flow
        // advances. payment_succeeded is intentionally not fired — the webhook
        // subscription_created remains the source of truth for a $0 conversion.
        Sentry.captureMessage(
          result.type === "no_payment_required"
            ? "Checkout: coupon zeroed the invoice, no payment required"
            : "Existing subscription found for customer",
          {
            level: "info",
            extra: {
              customerId: result.customerId,
              subscriptionId: result.subscriptionId,
            },
          }
        );
        onPaymentComplete({
          customerId: result.customerId,
          subscriptionId: result.subscriptionId,
        });
        return;
      }

      // The deferred-intent Elements group is mounted in "subscription" mode,
      // so it confirms with either intent type — follow the server's decision.
      const confirmIntent =
        result.type === "setup_intent"
          ? stripe.confirmSetup
          : stripe.confirmPayment;
      const confirmResult = await confirmIntent({
        elements,
        clientSecret: result.clientSecret,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: { billing_details: billingDetails },
        },
        redirect: "if_required",
      });

      if (confirmResult.error) {
        const userMessage = confirmResult.error.message
          ? `${confirmResult.error.message} Please try again or use a different payment method.`
          : "Payment confirmation failed. Please try again or use a different payment method.";
        setError(userMessage);
        setIsSubmitting(false);

        await tryCancelDanglingSubscription(result.subscriptionId);

        Sentry.captureException(confirmResult.error, {
          tags: {
            component: "stripe.elementsCheckout",
            stripe_operation: "confirm_payment",
          },
          extra: {
            type: result.type,
            subscriptionId: result.subscriptionId,
          },
        });
        return;
      }

      // Step 4: Payment succeeded — paired with payment_submitted above.
      // Server-side subscription_created (from the webhook) is the source
      // of truth for revenue, but client-side payment_succeeded gives an
      // immediate "user saw their card go through" signal independent of
      // webhook delay.
      logEvent({
        eventName: STATSIG_EVENTS.PAYMENT_SUCCEEDED,
        value: "card",
        metadata: paymentMetadata,
      });

      onPaymentComplete({
        customerId: result.customerId,
        subscriptionId: result.subscriptionId,
        isTrialing: result.type === "setup_intent",
      });
    } catch (err) {
      if (result) {
        await tryCancelDanglingSubscription(result.subscriptionId);
      }

      const message =
        err instanceof Error
          ? err.message
          : "Payment failed. Please try again.";
      setError(message);
      setIsSubmitting(false);
      onError?.(err instanceof Error ? err : new Error(message));

      Sentry.captureException(err, {
        tags: {
          component: "stripe.elementsCheckout",
          stripe_operation: "checkout_submit",
        },
        extra: {
          email: userInfo.email,
          priceId,
          productName,
        },
      });
    }
  }

  const buttonDisabled = isSubmitting || !stripe || !elements;

  return (
    <>
      <form id={FORM_ID} onSubmit={handleSubmit} className="pb-28">
        <PaymentElement
          onReady={() => onPaymentElementReady?.()}
          options={
            {
              layout: "tabs",
              paymentMethodOrder: ["card", "apple_pay", "google_pay"],
              wallets: {
                applePay: "auto",
                googlePay: "auto",
                link: "never",
              },
            } as StripePaymentElementOptions
          }
        />

        {enablePromoCodes && (
          <div className="pt-4">
            {appliedPromo ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm">
                <span className="text-foreground">
                  <span className="font-semibold">{appliedPromo.code}</span>
                  {appliedPromo.discountLabel
                    ? ` — ${appliedPromo.discountLabel}`
                    : ""}
                </span>
                <button
                  type="button"
                  onClick={handleRemovePromo}
                  className="text-xs font-medium text-primary underline hover:text-primary-hover"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => {
                    setPromoInput(e.target.value);
                    if (promoError) setPromoError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleApplyPromo();
                    }
                  }}
                  placeholder="Promo code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label="Promo code"
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-foreground-muted focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={!promoInput.trim() || promoChecking}
                  className="shrink-0 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface disabled:opacity-50"
                >
                  {promoChecking ? "Checking…" : "Apply"}
                </button>
              </div>
            )}
            {promoError && (
              <p className="pt-1 text-xs text-error" role="alert">
                {promoError}
              </p>
            )}
          </div>
        )}

        {renderTosCheckbox?.({
          checked: tosAccepted,
          onChange: (checked) => {
            setTosAccepted(checked);
            if (checked) setTosError(false);
          },
          error: tosError,
        })}

        {error && (
          <div className={errorClassName} role="alert">
            {error}
          </div>
        )}
      </form>

      {renderSubmitButton({
        isSubmitting,
        disabled: buttonDisabled,
        formId: FORM_ID,
      })}
    </>
  );
}

/**
 * ElementsCheckoutForm - Wraps CheckoutForm in <Elements> provider.
 *
 * Uses deferred intent creation: initializes Elements with mode/amount/currency,
 * then creates the subscription on submit for immediate inline confirmation.
 *
 * All visual styling is read from the ElementsThemeProvider wrapper.
 */
const ElementsCheckoutForm: React.FC<ElementsCheckoutFormProps> = (props) => {
  const { planPrice } = props;
  const { appearance, fonts } = useElementsTheme();
  const stripePromise = useMemo(() => getStripe(), []);

  const elementsOptions: StripeElementsOptions = useMemo(
    () => ({
      // Deferred-intent "subscription" mode can be confirmed with EITHER a
      // PaymentIntent or a SetupIntent, so the server alone owns the
      // trial-vs-immediate-charge decision (it zeroes trialDays for returning
      // customers — PE-1479) and the confirm step just follows result.type.
      // A fixed setup/payment mount mode chosen from the plan's nominal trial
      // desynced from that per-customer override and threw "collected in setup
      // mode, cannot be confirmed with a Payment Intent" for returning users.
      //
      // `amount` stays at planPrice unconditionally: when the server returns a
      // PaymentIntent it must match the PI; it's display-only when a
      // SetupIntent is confirmed. `setupFutureUsage: "off_session"` matches the
      // PI stamped by `save_default_payment_method: "on_subscription"` so
      // confirmPayment doesn't error on a setup_future_usage mismatch.
      mode: "subscription" as const,
      amount: planPrice,
      currency: "usd",
      setupFutureUsage: "off_session" as const,
      paymentMethodTypes: ["card"],
      appearance: appearance as StripeElementsOptions["appearance"],
      fonts: fonts,
    }),
    [planPrice, appearance, fonts]
  );

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default ElementsCheckoutForm;
